import { coerceNumberProperty, NumberInput } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, Input, OnDestroy } from '@angular/core';
import { ChannelUtil } from '@commons/util/helper/channel';
import { BehaviorSubject, distinctUntilChanged, map, of, switchMap, take, withLatestFrom } from 'rxjs';

import {
  AgentChannelFacade,
  AgentMailFacade,
  AgentSkillFacade,
  ChanEnum,
  Channel,
  ChannelType,
  distinctUntilSerializeChanged,
  isNotNullOrUndefined,
  PlanningFacade,
  UntilDestroy,
  untilDestroyed,
} from '@contact/upgrade';

@UntilDestroy()
@Component({
  selector: '[cng-agent-planning-row-channel]',
  templateUrl: 'agent-planning-row-channel.component.html',
  styleUrls: ['./agent-planning-row-channel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentPlanningRowChannelComponent implements OnDestroy {
  @Input()
  set channel(value: Channel) {
    if (!!value) {
      this._channel$.next(value);
    }
  }
  get channel$() {
    return this._channel$.pipe(distinctUntilSerializeChanged());
  }
  private readonly _channel$ = new BehaviorSubject<Channel | undefined>(undefined);

  @Input()
  set index(value: NumberInput) {
    if (!!value) {
      this._index$.next(coerceNumberProperty(value));
    }
  }
  get index$() {
    return this._index$.pipe(distinctUntilChanged());
  }
  private readonly _index$ = new BehaviorSubject<number>(-1);

  readonly activable$ = this.channel$.pipe(
    isNotNullOrUndefined(),
    switchMap(channel => this.agentChannelFacade.getActivable(channel))
  );
  readonly active$ = this.activable$.pipe(
    map(activable => !!activable && activable.active),
    distinctUntilChanged()
  );
  readonly canActive$ = this.activable$.pipe(
    map(activable => !!activable && activable.canActive),
    distinctUntilChanged()
  );
  readonly dirty$ = this.activable$.pipe(
    map(activable => !!activable && activable.dirty),
    distinctUntilChanged()
  );
  readonly channelStr$ = this.channel$.pipe(map(channel => ChannelUtil.toString(channel || ChanEnum.NotSet)));
  readonly isMail$ = this.channel$.pipe(map(channel => channel && ChannelUtil.hasChannelType([channel], ChannelType.MAIL)));
  readonly hasSkillActive$ = this.channel$.pipe(
    withLatestFrom(this.active$),
    switchMap(([channel, active]) => {
      if (!channel) {
        return of(active);
      }
      // otherwise use global skills status
      return this.agentSkillFacade.hasActive(channel);
    })
  );
  readonly hasSkillDirty$ = this.channel$.pipe(
    withLatestFrom(this.dirty$),
    switchMap(([channel, dirty]) => {
      if (!channel) {
        return of(dirty);
      }
      // otherwise use global skills status
      return this.agentSkillFacade.isDirty(channel);
    })
  );
  readonly underInstruction$ = this.channel$.pipe(
    switchMap(channel => {
      if (!channel) {
        return of(false);
      }
      // otherwise check global plannings status
      return this.planningFacade.hasOneUnderInstruction(channel);
    })
  );
  readonly push$ = this.agentMailFacade.push$;

  constructor(
    private readonly planningFacade: PlanningFacade,
    private readonly agentChannelFacade: AgentChannelFacade,
    private readonly agentSkillFacade: AgentSkillFacade,
    private readonly agentMailFacade: AgentMailFacade
  ) {}

  ngOnDestroy() {
    this._channel$.complete();
    this._index$.complete();
  }

  toogleSkillsActivation(newValue: boolean): void {
    this.channel$.pipe(take(1), untilDestroyed(this)).subscribe(channel => {
      if (!channel) {
        return;
      }
      this.agentSkillFacade.changeAllActivation(channel, newValue);
    });
  }

  toogleMailPushActivation(newValue: boolean): void {
    this.agentMailFacade.changePush(newValue);
  }
}
